import type { CalendarBooking, LockedPeriod } from '../api/schemas';

/** A calendar date as `YYYY-MM-DD`. */
export type IsoDate = string;

const DAY_MS = 86_400_000;

/** Parses `YYYY-MM-DD` to a UTC midnight timestamp, independent of the machine zone. */
function toUtc(iso: IsoDate): number {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

function fromUtc(ms: number): IsoDate {
  return new Date(ms).toISOString().slice(0, 10);
}

function addDays(iso: IsoDate, n: number): IsoDate {
  return fromUtc(toUtc(iso) + n * DAY_MS);
}

/** Monday = 0 ... Sunday = 6. */
function mondayIndex(ms: number): number {
  return (new Date(ms).getUTCDay() + 6) % 7;
}

/** Returns the Monday-first cells covering `month` (1-12), in whole weeks: 28, 35 or 42 days. */
export function monthDays(year: number, month: number): IsoDate[] {
  const first = Date.UTC(year, month - 1, 1);
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const lead = mondayIndex(first);
  const weeks = Math.ceil((lead + daysInMonth) / 7);
  const start = fromUtc(first - lead * DAY_MS);
  return Array.from({ length: weeks * 7 }, (_, i) => addDays(start, i));
}

/** `from` is the first cell and `to` is the day after the last cell. */
export function gridRange(days: IsoDate[]): { from: IsoDate; to: IsoDate } {
  const from = days[0];
  const last = days[days.length - 1];
  if (from === undefined || last === undefined) throw new Error('gridRange needs at least one day');
  return { from, to: addDays(last, 1) };
}

export interface DayHalf {
  /** A booking id, or a VisBook period id for a block: unique only together with `type`. */
  bookingId: number;
  type: CalendarBooking['type'];
}

export interface DayCell {
  date: IsoDate;
  inMonth: boolean;
  isToday: boolean;
  full?: DayHalf;
  leaving?: DayHalf;
  arriving?: DayHalf;
  /** The unit is free this night but every unit of its type is taken. */
  locked?: boolean;
}

/** Builds the cells for a grid from `monthDays`; `month` is 1-12 (October = 10). */
export function buildCells(
  days: IsoDate[],
  month: number,
  today: IsoDate,
  bookings: CalendarBooking[],
  locked: LockedPeriod[] = []
): DayCell[] {
  const monthPart = String(month).padStart(2, '0');
  return days.map((date) => {
    const cell: DayCell = {
      date,
      inMonth: date.slice(5, 7) === monthPart,
      isToday: date === today
    };
    for (const b of bookings) {
      if (b.from < date && date < b.to) cell.full ??= { bookingId: b.id, type: b.type };
    }
    if (cell.full) return cell;
    for (const b of bookings) {
      const half: DayHalf = { bookingId: b.id, type: b.type };
      if (b.from === date) cell.arriving ??= half;
      if (b.to === date) cell.leaving ??= half;
    }
    // A booking or block on the unit wins: only a night the unit has free shows as locked.
    if (!cell.arriving && locked.some((l) => l.from <= date && date < l.to)) {
      cell.locked = true;
    }
    return cell;
  });
}

/** Number of nights in a stay `[from, to)`. */
export function nights(from: IsoDate, to: IsoDate): number {
  return Math.round((toUtc(to) - toUtc(from)) / DAY_MS);
}

/** True for the current month through +12 months; `month` is 1-12. */
export function canGoTo(year: number, month: number, today: IsoDate): boolean {
  const [ty = 0, tm = 1] = today.split('-').map(Number);
  const diff = year * 12 + (month - 1) - (ty * 12 + (tm - 1));
  return diff >= 0 && diff <= 12;
}
